import {
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
} from '@mui/material';
import { useStyles } from './styles/ReportTable.styles';
import { gatherColumns } from '@utils/summaries';
import { ReportTableHeader } from '../ReportTableHeader';
import { styleNumber } from '@utils/styles';

import type { ReportTableOptions } from './ReportLayout';
import React, { type ReactNode } from 'react';

interface ReportTableProps {
    reportName: string;
    tableOptions: ReportTableOptions;
    loading: boolean;
    height: number;
    noResultsText?: ReactNode;
}

export const ReportTableCustomSort = ({
    reportName,
    tableOptions,
    loading,
    height,
    noResultsText = 'There is no data to show',
}: ReportTableProps) => {
    const classes = useStyles();

    return (
        <Paper elevation={0} className={classes.wrapper} style={{ height: `${height} !important` }}>
            {loading ? (
                <div className={classes.loadingIndicator}>
                    <CircularProgress />
                </div>
            ) : (
                <TableContainer className={classes.container}>
                    <Table stickyHeader aria-label='sticky table'>
                        <TableHead>
                            <TableRow>
                                {tableOptions.headers.map((header: string, index: number) => (
                                    <ReportTableHeader
                                        key={`header - ${reportName} - ${header} - ${index}`}
                                        header={header}
                                    />
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {tableOptions.data.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={tableOptions.headers.length}
                                        height={35}
                                        style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                        {noResultsText}
                                    </TableCell>
                                </TableRow>
                            )}

                            {tableOptions.data.map((row: (string | number | React.ReactNode)[], idx: number) => {
                                const header = tableOptions.headers[idx];

                                return (
                                    <TableRow
                                        key={`row - ${reportName} - ${header} - ${idx}`}
                                        className={classes.tableRow}>
                                        {row.map((item: string | number | React.ReactNode, idx: number) => {
                                            return (
                                                <TableCell
                                                    key={`${reportName} - ${header} - ${item} - ${idx}`}
                                                    align='left'>
                                                    {item}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>

                        <TableFooter className={classes.tableFooter}>
                            <TableRow>
                                {tableOptions.data.length > 0 &&
                                    tableOptions.headers.map((header: string, idx: number) => {
                                        const summaryFunc = tableOptions.summaries[idx];

                                        const columns = gatherColumns(tableOptions.data, idx);

                                        const summary = summaryFunc ? summaryFunc(columns) : '';

                                        return (
                                            <TableCell
                                                key={`${reportName} - ${header} - ${summary}`}
                                                align='left'
                                                height={27}>
                                                {styleNumber(summary)}
                                            </TableCell>
                                        );
                                    })}
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};
